'use client';

import { c as _c } from "react/compiler-runtime";
import { SvgChevronright, SvgFourLeafClover } from '@common/icon';
import { cn } from '@core/utils';
import { type ReactNode, Suspense } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { ROUTES } from '@/constants';
import { useCustomRouter } from '@/hooks/useCustomRouter';
import { useBenefitGate } from '../../_hooks/useBenefitGate';
import { useLuckyTicketCount } from '../_hooks/useLuckyTicketCount';
const CARD_BUTTON_CLASS_NAME = 'flex w-full items-center gap-16 rounded-small bg-Gray-50 p-16 text-left transition-colors focus-visible:bg-Gray-100 enabled:active:bg-Gray-100 disabled:cursor-not-allowed xl:border xl:border-Gray-100 xl:bg-Gray-white xl:enabled:hover:border-Gray-300 xl:enabled:hover:bg-Gray-50 xl:focus-visible:border-Gray-300 xl:focus-visible:bg-Gray-50';
interface LuckyTicketButtonProps {
  onClick: () => void;
  disabled?: boolean;
  count: ReactNode;
  countClassName: string;
}
const LuckyTicketButton = t0 => {
  const $ = _c(12);
  const {
    onClick,
    disabled,
    count,
    countClassName
  } = t0;
  let t1;
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <SvgFourLeafClover className="size-24" originalFill={true} />;
    t2 = <span className="flex-1 font-700 text-Gray-900 typo-body4">행운권</span>;
    $[0] = t1;
    $[1] = t2;
  } else {
    t1 = $[0];
    t2 = $[1];
  }
  let t3;
  if ($[2] !== countClassName) {
    t3 = cn("flex items-center gap-4 font-600 typo-caption1", countClassName);
    $[2] = countClassName;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== count || $[5] !== t3) {
    t4 = <span className={t3}>{count}장 열기</span>;
    $[4] = count;
    $[5] = t3;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <SvgChevronright className="size-16 text-Gray-400" />;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  let t6;
  if ($[8] !== disabled || $[9] !== onClick || $[10] !== t4) {
    t6 = <button type="button" onClick={onClick} disabled={disabled} className={CARD_BUTTON_CLASS_NAME}>{t1}{t2}{t4}{t5}</button>;
    $[8] = disabled;
    $[9] = onClick;
    $[10] = t4;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  return t6;
};

//* 빈 상태 캡션 줄은 로딩 폴백·잔량 확정 상태 모두에서 상시 예약 — 0장 판명 순간 아래 콘텐츠가 밀리지 않게.
const EmptyTicketNotice = t0 => {
  const $ = _c(5);
  const {
    visible
  } = t0;
  const t1 = !visible;
  const t2 = !visible;
  let t3;
  if ($[0] !== t2) {
    t3 = cn("text-center font-600 text-Gray-500 typo-caption1", {
      invisible: t2
    });
    $[0] = t2;
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  let t4;
  if ($[2] !== t1 || $[3] !== t3) {
    t4 = <p aria-hidden={t1} className={t3}>행운권을 모으면 열 수 있어요</p>;
    $[2] = t1;
    $[3] = t3;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  return t4;
};
const AuthedLuckyTicketButton = t0 => {
  const $ = _c(10);
  const {
    onClick
  } = t0;
  const {
    data: luckyTicketCount
  } = useLuckyTicketCount();
  const isEmpty = luckyTicketCount <= 0;
  const t1 = isEmpty ? "text-Gray-400" : "text-O-550";
  let t2;
  if ($[0] !== isEmpty || $[1] !== luckyTicketCount || $[2] !== onClick || $[3] !== t1) {
    t2 = <LuckyTicketButton onClick={onClick} disabled={isEmpty} count={luckyTicketCount} countClassName={t1} />;
    $[0] = isEmpty;
    $[1] = luckyTicketCount;
    $[2] = onClick;
    $[3] = t1;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== isEmpty) {
    t3 = <EmptyTicketNotice visible={isEmpty} />;
    $[5] = isEmpty;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== t2 || $[8] !== t3) {
    t4 = <>{t2}{t3}</>;
    $[7] = t2;
    $[8] = t3;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  return t4;
};
interface LuckyTicketCardProps {
  isGuest: boolean;
}
export const LuckyTicketCard = t0 => {
  const $ = _c(10);
  const {
    isGuest
  } = t0;
  const {
    triggerGate,
    gateModals
  } = useBenefitGate();
  const router = useCustomRouter();
  let t1;
  if ($[0] !== router || $[1] !== triggerGate) {
    t1 = () => {
      triggerGate(["app", "login", "adult", "consent"], () => router.push(ROUTES.BENEFIT_SCRATCH.pathname));
    };
    $[0] = router;
    $[1] = triggerGate;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const handleClick = t1;
  let t2;
  if ($[3] !== handleClick || $[4] !== isGuest) {
    t2 = isGuest ? <LuckyTicketButton onClick={handleClick} count={<span aria-label={"\uB85C\uADF8\uC778 \uD6C4 \uD655\uC778 \uAC00\uB2A5"}>???</span>} countClassName="text-Gray-300" /> : <ErrorBoundary fallback={<><LuckyTicketButton onClick={handleClick} count="-" countClassName="text-O-550" /><EmptyTicketNotice visible={false} /></>}><Suspense fallback={<><LuckyTicketButton onClick={handleClick} count="-" countClassName="text-O-550" /><EmptyTicketNotice visible={false} /></>}><AuthedLuckyTicketButton onClick={handleClick} /></Suspense></ErrorBoundary>;
    $[3] = handleClick;
    $[4] = isGuest;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <p className="pl-8 font-600 text-Gray-600 typo-caption1">※ 행운권은 수령 당일에만 유효하며, 당일 미개봉 시 자동 소멸됩니다.</p>;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== gateModals || $[8] !== t2) {
    t4 = <div className="flex flex-col gap-4">{t2}{t3}{gateModals}</div>;
    $[7] = gateModals;
    $[8] = t2;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  return t4;
};